import _slicedToArray from "/Users/joeyanuff/Development/miedo-react/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import makeHistory from "./history.js";
import makeMatcher from "./matcher.js";
import { useRef, useMemo, useEffect, useState, useContext, useCallback, createContext, isValidElement, cloneElement, createElement as h, Children } from "react";
/*
 * Part 1, Hooks API: useRouter, useRoute and useLocation
 */

var RouterCtx = createContext();
export var buildRouter = function buildRouter() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    history: options.history || makeHistory(),
    matcher: options.matcher || makeMatcher()
  };
};
export var useRouter = function useRouter() {
  var providedRouter = useContext(RouterCtx); // either obtain the router from the outer context
  // (provided by the `<Router /> component) or create
  // a default one on demand.

  var router = useMemo(function () {
    return providedRouter ? providedRouter : buildRouter();
  }, [providedRouter]);
  return router;
};
export var useLocation = function useLocation() {
  var history = useRouter().history;

  var _useState = useState(history.path()),
      _useState2 = _slicedToArray(_useState, 2),
      location = _useState2[0],
      update = _useState2[1]; // subscribe to history updates


  useEffect(function () {
    return history.subscribe(function (x) {
      return update(x);
    });
  }, [history]);
  var pushLocation = useCallback(function (y) {
    return history.push(y);
  }, [history]);
  return [location, pushLocation];
};
export var useRoute = function useRoute(pattern) {
  var router = useRouter();

  var _useLocation = useLocation(),
      _useLocation2 = _slicedToArray(_useLocation, 1),
      path = _useLocation2[0];

  return router.matcher(pattern, path);
};
/*
 * Part 2, Low Carb Router API: Router, Route, Link, Switch
 */

export var Router = function Router(props) {
  var ref = useRef(null); // this little trick allows to avoid having unnecessary
  // calls to potentially expensive `buildRouter` method.
  // https://reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily

  function getRouter() {
    if (ref.current !== null) {
      return ref.current;
    } else {
      return ref.current = buildRouter(props);
    }
  }

  return h(RouterCtx.Provider, {
    value: getRouter(),
    children: props.children
  });
};
export var Route = function Route(props) {
  var _useRoute = useRoute(props.path),
      _useRoute2 = _slicedToArray(_useRoute, 2),
      matches = _useRoute2[0],
      params = _useRoute2[1];

  if (!matches && !props.match) {
    return null;
  } // React-Router style `component` prop


  if (props.component) {
    return h(props.component, {
      params: params
    });
  } // support render prop or plain children


  return typeof props.children === "function" ? props.children(params) : props.children;
};
export var Link = function Link(props) {
  var _useLocation3 = useLocation(),
      _useLocation4 = _slicedToArray(_useLocation3, 2),
      navigate = _useLocation4[1];

  var href = props.href || props.to;
  var children = props.children,
      onClick = props.onClick;
  var handleClick = useCallback(function (event) {
    event.preventDefault();
    navigate(href);
    onClick && onClick();
  }, [href, onClick, navigate]); // wraps children in `a` if needed

  var extraProps = {
    href: href,
    onClick: handleClick,
    to: null
  };
  var jsx = isValidElement(children) ? children : h("a", props);
  return cloneElement(jsx, extraProps);
};
export var Switch = function Switch(_ref) {
  var children = _ref.children,
      location = _ref.location;

  var _useRouter = useRouter(),
      matcher = _useRouter.matcher;

  var _useLocation5 = useLocation(),
      _useLocation6 = _slicedToArray(_useLocation5, 1),
      originalLocation = _useLocation6[0];

  var element = null; // this looks similar to Switch implementation from React Router
  // https://github.com/ReactTraining/react-router/blob/master/packages/react-router/modules/Switch.js

  Children.forEach(children, function (child) {
    if (!element && isValidElement(child)) {
      var _matcher = matcher(child.props.path, location || originalLocation),
          _matcher2 = _slicedToArray(_matcher, 1),
          match = _matcher2[0];

      if (match) element = child;
    }
  });
  return element ? cloneElement(element, {
    match: true
  }) : null;
};
export var Redirect = function Redirect(props) {
  var _useLocation7 = useLocation(),
      _useLocation8 = _slicedToArray(_useLocation7, 2),
      push = _useLocation8[1];

  useEffect(function () {
    return push(props.href || props.to);
  });
  return null;
};
export default useRoute;